import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { ErrorProperties } from '../../API/identityAPIProperties';
import API from '../../API/Main/API';
import { resetState } from '../../resetState';
import { AssignBCBAsProperties, AssignClinicalSupervisorProperties, UserProperties } from '../../API/userAPIProperties';
import { TherapistProperties } from '../../API/ClientAPIHelpers/clientProperties';

export const getClinicalSupervisorsTherapists = createAsyncThunk(
	'user/clinical-supervisor/therapists',
	async (userId: string, thunkAPI) => {
		const response = await API.UserAPI.ClinicalSupervisor.getClinicalSupervisorsTherapists(userId);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response as TherapistProperties[];
	},
);

export const getClinicalSupervisorsBCBAs = createAsyncThunk(
	'user/clinical-supervisor/bcbas',
	async (userId: string, thunkAPI) => {
		const response = await API.UserAPI.ClinicalSupervisor.getClinicalSupervisorsBCBAs(userId);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response as UserProperties[];
	},
);

export const getBCBAClinicalSupervisors = createAsyncThunk(
	'user/bcba/clinical-supervisors',
	async (userId: string, thunkAPI) => {
		const response = await API.UserAPI.ClinicalSupervisor.getBCBAClinicalSupervisors(userId);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response as TherapistProperties[];
	},
);

export const assignClinicalSupervisors = createAsyncThunk(
	'user/assign/clinical-supervisor/bcbas',
	async (data: AssignClinicalSupervisorProperties, thunkAPI) => {
		const response = await API.UserAPI.ClinicalSupervisor.assignClinicalSupervisors(data);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response;
	},
);

export const assignBcbas = createAsyncThunk(
	'user/assign/bcba/clinical-supervisors',
	async (data: AssignBCBAsProperties, thunkAPI) => {
		const response = await API.UserAPI.ClinicalSupervisor.assignBCBAs(data);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response;
	},
);

interface ClinicalSupervisorInitialStateProperties {
	bcbasClinicalSupervisors: TherapistProperties[];
	clinicalSupervisorsBcbas: UserProperties[] | null;
	clinicalSupervisorsTherapists: TherapistProperties[];
	error: ErrorProperties;

	loading: boolean;
}

const initialClinicalSupervisorState: ClinicalSupervisorInitialStateProperties = {
	loading: false,
	error: {
		status: 0,
		title: '',
	},
	clinicalSupervisorsBcbas: null,
	bcbasClinicalSupervisors: [],
	clinicalSupervisorsTherapists: [],
};

const clinicalSupervisorSlice = createSlice({
	name: 'clinicalSupervisor',
	initialState: initialClinicalSupervisorState,
	reducers: {},
	extraReducers: (builder) => {
		// getClinicalSupervisorsTherapists
		builder.addCase(getClinicalSupervisorsTherapists.pending, (state) => {
			state.loading = true;
		});
		builder.addCase(getClinicalSupervisorsTherapists.fulfilled, (state, action) => {
			state.loading = false;
			state.clinicalSupervisorsTherapists = action.payload;
		});
		builder.addCase(getClinicalSupervisorsTherapists.rejected, (state, action) => {
			state.loading = false;
			state.error = action.payload as ErrorProperties;
		});
		// getClinicalSupervisorsBCBAs
		builder.addCase(getClinicalSupervisorsBCBAs.pending, (state) => {
			state.loading = true;
		});
		builder.addCase(getClinicalSupervisorsBCBAs.fulfilled, (state, action) => {
			state.loading = false;
			state.clinicalSupervisorsBcbas = action.payload;
		});
		builder.addCase(getClinicalSupervisorsBCBAs.rejected, (state, action) => {
			state.loading = false;
			state.error = action.payload as ErrorProperties;
		});
		// getBCBAClinicalSupervisors
		builder.addCase(getBCBAClinicalSupervisors.pending, (state) => {
			state.loading = true;
		});
		builder.addCase(getBCBAClinicalSupervisors.fulfilled, (state, action) => {
			state.loading = false;
			state.bcbasClinicalSupervisors = action.payload;
		});
		builder.addCase(getBCBAClinicalSupervisors.rejected, (state, action) => {
			state.loading = false;
			state.error = action.payload as ErrorProperties;
		});
		// assignClinicalSupervisors
		builder.addCase(assignClinicalSupervisors.pending, (state) => {
			state.loading = true;
			state.error = initialClinicalSupervisorState.error;
		});
		builder.addCase(assignClinicalSupervisors.fulfilled, (state) => {
			state.loading = false;
		});
		builder.addCase(assignClinicalSupervisors.rejected, (state, action) => {
			state.loading = false;
			state.error = action.payload as ErrorProperties;
		});
		// assignBcbas
		builder.addCase(assignBcbas.pending, (state) => {
			state.loading = true;
			state.error = initialClinicalSupervisorState.error;
		});
		builder.addCase(assignBcbas.fulfilled, (state) => {
			state.loading = false;
		});
		builder.addCase(assignBcbas.rejected, (state, action) => {
			state.loading = false;
			state.error = action.payload as ErrorProperties;
		});

		// reset state
		builder.addCase(resetState, () => initialClinicalSupervisorState);
	},
});

export default clinicalSupervisorSlice.reducer;
